import { useEffect, useState, useCallback, useMemo } from 'react';
import {
    AttributeDefinition,
    CreateAttributeDefinitionRequest,
    UpdateAttributeDefinitionRequest,
    fetchAttributeDefinitions as apiFetchAttributeDefinitions,
    createAttributeDefinition as apiCreateAttributeDefinition,
    updateAttributeDefinition as apiUpdateAttributeDefinition,
    deleteAttributeDefinition as apiDeleteAttributeDefinition,
} from '../api/AttributeDefinitionApi';
import { ApiError } from '../api/types';

interface UseAttributeDefinitionsReturn {
    // All attributes (enabled and disabled)
    attributeDefinitions: AttributeDefinition[];

    // Enabled attributes only
    enabledAttributeDefinitions: AttributeDefinition[];

    // Disabled attributes only
    disabledAttributeDefinitions: AttributeDefinition[];

    // Filtered lists
    filterableAttributeDefinitions: AttributeDefinition[];
    tableAttributeDefinitions: AttributeDefinition[];
    formAttributeDefinitions: AttributeDefinition[];

    // Loading states
    loading: boolean;
    error: string | null;

    // Action states
    creating: boolean;
    updating: boolean;
    deleting: boolean;

    // Actions
    create: (attributeData: CreateAttributeDefinitionRequest) => Promise<AttributeDefinition | null>;
    update: (name: string, attributeData: UpdateAttributeDefinitionRequest) => Promise<AttributeDefinition | null>;
    remove: (name: string) => Promise<void>;
    refresh: () => Promise<void>;

    // Utility functions
    getAttributeDefinitionByName: (name: string) => AttributeDefinition | undefined;
    getEnabledAttributeDefinitionByName: (name: string) => AttributeDefinition | undefined;
    isAttributeEnabled: (name: string) => boolean;
}

export function useAttributeDefinitions(entity: string = 'Employee'): UseAttributeDefinitionsReturn {

    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Individual action states
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadAttributeDefinitions();
    }, [entity]);

    const loadAttributeDefinitions = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetchAttributeDefinitions(entity);
            const attributeDefinitions: AttributeDefinition[] = response.data || [];
            attributeDefinitions.sort((a, b) =>
                a.label.localeCompare(b.label)
            );
            setAttributeDefinitions(attributeDefinitions);

        } catch (error) {
            console.error('Error loading attribute definitions:', error);
            setError('Failed to load attribute definitions');
            setAttributeDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const refresh = useCallback(async () => {
        await loadAttributeDefinitions();
    }, [entity]);

    // Separate enabled and disabled attributes
    const enabledAttributeDefinitions = useMemo(() => {
        return attributeDefinitions.filter(def => def.enabled);
    }, [attributeDefinitions]);

    const disabledAttributeDefinitions = useMemo(() => {
        return attributeDefinitions.filter(def => !def.enabled);
    }, [attributeDefinitions]);

    const filterableAttributeDefinitions = useMemo(() => {
        return enabledAttributeDefinitions.filter(def => def.configuration.filterable);
    }, [enabledAttributeDefinitions]);

    const tableAttributeDefinitions = useMemo(() => {
        return enabledAttributeDefinitions.filter(def => def.configuration.tableColumn);
    }, [enabledAttributeDefinitions]);

    const formAttributeDefinitions = useMemo(() => {
        return enabledAttributeDefinitions.filter(def => def.configuration.includeInCreate);
    }, [enabledAttributeDefinitions]);

    // Cache for ALL attributes: name -> AttributeDefinition
    const attributeDefinitionMap = useMemo(() => {
        const map = new Map<string, AttributeDefinition>();
        attributeDefinitions.forEach(def => {
            map.set(def.name, def);
        });
        return map;
    }, [attributeDefinitions]);

    // Cache for enabled attributes only: name -> AttributeDefinition
    const enabledAttributeDefinitionMap = useMemo(() => {
        const map = new Map<string, AttributeDefinition>();
        enabledAttributeDefinitions.forEach(def => {
            map.set(def.name, def);
        });
        return map;
    }, [enabledAttributeDefinitions]);

    // Get attribute definition by name (searches all attributes)
    const getAttributeDefinitionByName = useCallback((name: string): AttributeDefinition | undefined => {
        return attributeDefinitionMap.get(name);
    }, [attributeDefinitionMap]);

    // Get enabled attribute definition by name
    const getEnabledAttributeDefinitionByName = useCallback((name: string): AttributeDefinition | undefined => {
        return enabledAttributeDefinitionMap.get(name);
    }, [enabledAttributeDefinitionMap]);

    // Check if an attribute is enabled
    const isAttributeEnabled = useCallback((name: string): boolean => {
        return enabledAttributeDefinitionMap.has(name);
    }, [enabledAttributeDefinitionMap]);

    // Private: Add attribute to list
    const addAttribute = useCallback((attribute: AttributeDefinition) => {
        setAttributeDefinitions(prev => [attribute, ...prev]);
    }, []);

    // Private: Remove attribute from list
    const removeAttribute = useCallback((name: string) => {
        setAttributeDefinitions(prev => prev.filter(attr => attr.name !== name));
    }, []);

    // Private: Update attribute in list
    const updateAttributeInList = useCallback((name: string, updates: Partial<AttributeDefinition>) => {
        setAttributeDefinitions(prev =>
            prev.map(attr => (attr.name === name ? { ...attr, ...updates } : attr))
        );
    }, []);

    // Public: Create attribute definition
    const create = useCallback(async (attributeData: CreateAttributeDefinitionRequest): Promise<AttributeDefinition | null> => {
        setCreating(true);
        try {
            const response = await apiCreateAttributeDefinition(entity, attributeData);

            if (response.success && response.data) {
                addAttribute(response.data);
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to create attribute definition');
            }
        } catch (err) {
            console.error('Error creating attribute definition:', err);
            throw err;
        } finally {
            setCreating(false);
        }
    }, [entity, addAttribute]);

    // Public: Update attribute definition
    const update = useCallback(async (name: string, attributeData: UpdateAttributeDefinitionRequest): Promise<AttributeDefinition | null> => {
        setUpdating(true);
        try {
            const response = await apiUpdateAttributeDefinition(entity, name, attributeData);

            if (response.success && response.data) {
                updateAttributeInList(name, response.data);
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to update attribute definition');
            }
        } catch (err) {
            console.error('Error updating attribute definition:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    }, [entity, updateAttributeInList]);

    // Public: Delete attribute definition
    const remove = useCallback(async (name: string) => {
        setDeleting(true);
        try {
            const response = await apiDeleteAttributeDefinition(entity, name);

            if (response.success) {
                removeAttribute(name);
            } else {
                throw new ApiError(response.error, 'Failed to delete attribute definition');
            }
        } catch (err) {
            console.error('Error deleting attribute definition:', err);
            throw err;
        } finally {
            setDeleting(false);
        }
    }, [entity, removeAttribute]);

    return {
        attributeDefinitions,
        enabledAttributeDefinitions,
        disabledAttributeDefinitions,
        filterableAttributeDefinitions,
        tableAttributeDefinitions,
        formAttributeDefinitions,
        loading,
        error,
        creating,
        updating,
        deleting,
        create,
        update,
        remove,
        refresh,
        getAttributeDefinitionByName,
        getEnabledAttributeDefinitionByName,
        isAttributeEnabled,
    };
}

export default useAttributeDefinitions;